/**
 * HeatingSystemPanel — native rewrite of the legacy HeatingSystem sub-tab (the heaviest
 * piece). Holds the heater view-model array in `useState`, seeded ONCE from the persisted
 * DTO via the codec's `decodeHeaters` (an empty DTO yields ONE default heater card —
 * collection-default-on-read, NOT a mount write). Each heater/billing edit updates the
 * local VM AND commits the encoded `heaters` array to the DTO via `patch({ heaters })`
 * (the `heaters`-scoped field-granular write path — C1). Rows are keyed by STABLE local
 * ids (C6), so delete/reorder never cross-contaminate leaf-local mid-edit state.
 *
 * The VM is the render + stable-id source; the DTO is the persistence + completeness
 * source. Both stay in sync because every mutation writes both. No mount write, no
 * parallel store slice.
 */

import React, { useCallback, useState } from 'react'
import { Fade, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded'
import Collapse from '@mui/material/Collapse'
import { TransitionGroup } from 'react-transition-group'

import { MUIButton } from 'components/common/MUIButton'
import { MUIDivider } from 'components/common/MUIDivider'

import type { CartItemId } from 'models/purchaseFlowRevamp'

import { HeaterCard } from './HeaterCard'
import { useConsumptionCertificateConfig } from '../useConsumptionCertificateConfig'
import {
  decodeHeaters,
  encodeHeaters,
  makeDefaultHeaterVM,
  makeLocalId,
  type BillingPeriodVM,
  type HeaterVM
} from '../consumptionCertificate.codec'

export interface HeatingSystemPanelProps {
  cartItemId: CartItemId
}

const makeDefaultBillingPeriodVM = (): BillingPeriodVM => ({
  localId: makeLocalId('billing'),
  startDate: null,
  endDate: null,
  consumption: '',
  hotWaterConsumptionKwh: null,
})

export const HeatingSystemPanel: React.FC<HeatingSystemPanelProps> = ({ cartItemId }) => {
  const { t } = useTranslation(['heating_system'])
  const { value, patch } = useConsumptionCertificateConfig(cartItemId)

  // Seeded ONCE from the persisted DTO (empty → one default heater card).
  const [heaters, setHeaters] = useState<HeaterVM[]>(() => decodeHeaters(value.heaters))

  // Commit: update the local VM + write the encoded array to the DTO (heaters-scoped
  // field patch — C1). Lazy `updater` form so rapid edits compose correctly.
  const commit = useCallback(
    (updater: (prev: HeaterVM[]) => HeaterVM[]) => {
      setHeaters((prev) => {
        const next = updater(prev)
        patch({ heaters: encodeHeaters(next) })
        return next
      })
    },
    [patch]
  )

  const addHeater = useCallback(() => commit((prev) => [...prev, makeDefaultHeaterVM()]), [commit])

  const removeHeater = useCallback(
    (localId: string) => commit((prev) => prev.filter((h) => h.localId !== localId)),
    [commit]
  )

  const updateHeater = useCallback(
    (localId: string, partial: Partial<HeaterVM>) =>
      commit((prev) => prev.map((h) => (h.localId === localId ? { ...h, ...partial } : h))),
    [commit]
  )

  const addBillingPeriod = useCallback(
    (heaterLocalId: string) =>
      commit((prev) =>
        prev.map((h) =>
          h.localId === heaterLocalId
            ? { ...h, billingPeriodItems: [...h.billingPeriodItems, makeDefaultBillingPeriodVM()] }
            : h
        )
      ),
    [commit]
  )

  const updateBillingPeriod = useCallback(
    (heaterLocalId: string, billingLocalId: string, partial: Partial<BillingPeriodVM>) =>
      commit((prev) =>
        prev.map((h) =>
          h.localId === heaterLocalId
            ? {
                ...h,
                billingPeriodItems: h.billingPeriodItems.map((b) =>
                  b.localId === billingLocalId ? { ...b, ...partial } : b
                ),
              }
            : h
        )
      ),
    [commit]
  )

  const removeBillingPeriod = useCallback(
    (heaterLocalId: string, billingLocalId: string) =>
      commit((prev) =>
        prev.map((h) =>
          h.localId === heaterLocalId
            ? { ...h, billingPeriodItems: h.billingPeriodItems.filter((b) => b.localId !== billingLocalId) }
            : h
        )
      ),
    [commit]
  )

  return (
    <Fade in>
      <Stack gap={2}>

        <TransitionGroup component={null}>
          {heaters.map((heater, index) => (
            <Collapse key={heater.localId}>
              <HeaterCard
                heater={heater}
                index={index}
                onRemove={() => removeHeater(heater.localId)}
                onUpdate={(partial) => updateHeater(heater.localId, partial)}
                onAddBillingPeriod={() => addBillingPeriod(heater.localId)}
                onUpdateBillingPeriod={(billingLocalId, partial) =>
                  updateBillingPeriod(heater.localId, billingLocalId, partial)
                }
                onRemoveBillingPeriod={(billingLocalId) => removeBillingPeriod(heater.localId, billingLocalId)}
              />
            </Collapse>
          ))}
        </TransitionGroup>

        <MUIDivider>
          <MUIButton
            type="secondaryBorder"
            startIcon={<AddCircleOutlineRoundedIcon fontSize="large" />}
            onClick={addHeater}
          >
            {t('add_heating_system')}
          </MUIButton>
        </MUIDivider>

      </Stack>
    </Fade>
  )
}
